import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { orderAPI } from '../api/services';
import Loading from '../components/common/Loading';
import toast from 'react-hot-toast';
import { FiPackage, FiChevronRight } from 'react-icons/fi';
import './Orders.css';

function OrderList() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    orderAPI.getAll().then(res => setOrders(res.data.data.content || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;

  if (orders.length === 0) {
    return (
      <div className="empty-state">
        <FiPackage size={48} />
        <h2>No orders yet</h2>
        <p>Start shopping and your orders will appear here.</p>
        <Link to="/shop" className="btn btn-primary">Shop Now</Link>
      </div>
    );
  }

  return (
    <div className="orders-page">
      <h1>My Orders</h1>
      <div className="orders-list">
        {orders.map(o => (
          <Link to={`/orders/${o.id}`} key={o.id} className="order-card">
            <div className="oc-header">
              <span className="oc-number">#{o.orderNumber}</span>
              <span className={`oc-status status-${o.status?.toLowerCase()}`}>{o.status}</span>
            </div>
            <div className="oc-body">
              <p>{o.items?.length || 0} items</p>
              <p className="oc-total">₹{o.totalAmount}</p>
              <span className="oc-date">{new Date(o.createdAt).toLocaleDateString()}</span>
            </div>
            <FiChevronRight className="oc-arrow" />
          </Link>
        ))}
      </div>
    </div>
  );
}

function OrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    orderAPI.getById(id).then(res => setOrder(res.data.data)).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  const handleCancel = async () => {
    try {
      await orderAPI.cancel(id);
      const res = await orderAPI.getById(id);
      setOrder(res.data.data);
      toast.success('Order cancelled');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Cannot cancel');
    }
  };

  if (loading) return <Loading />;
  if (!order) return <div className="empty-state"><h3>Order not found</h3></div>;

  return (
    <div className="order-detail-page">
      <Link to="/orders" className="back-link">← Back to Orders</Link>
      <div className="od-header">
        <div>
          <h1>Order #{order.orderNumber}</h1>
          <p className="od-date">Placed on {new Date(order.createdAt).toLocaleDateString()}</p>
        </div>
        <span className={`oc-status status-${order.status?.toLowerCase()}`}>{order.status}</span>
      </div>

      <div className="od-layout">
        <div className="od-items">
          <h3>Items</h3>
          {order.items?.map(item => (
            <div key={item.id} className="od-item">
              <div className="odi-img">
                <div className="odi-no-img">No Img</div>
              </div>
              <div className="odi-info">
                <p className="odi-name">{item.productName}</p>
                <p className="odi-variant">{item.size && `Size: ${item.size}`} {item.color && `Color: ${item.color}`}</p>
                <p>Qty: {item.quantity} × ₹{item.unitPrice}</p>
              </div>
              <p className="odi-total">₹{item.totalPrice}</p>
            </div>
          ))}
        </div>
        <div className="od-sidebar">
          <div className="od-summary">
            <h3>Summary</h3>
            <div className="os-row"><span>Subtotal</span><span>₹{order.subtotal}</span></div>
            {order.discountAmount > 0 && <div className="os-row"><span>Discount</span><span>-₹{order.discountAmount}</span></div>}
            <div className="os-row"><span>Shipping</span><span>₹{order.shippingAmount || 0}</span></div>
            <hr />
            <div className="os-row total"><span>Total</span><span>₹{order.totalAmount}</span></div>
          </div>
          {order.shippingAddress && (
            <div className="od-address">
              <h3>Shipping Address</h3>
              <p><strong>{order.shippingAddress.fullName}</strong></p>
              <p>{order.shippingAddress.addressLine1}</p>
              <p>{order.shippingAddress.city}, {order.shippingAddress.state} - {order.shippingAddress.postalCode}</p>
              <p>{order.shippingAddress.phone}</p>
            </div>
          )}
          {order.trackingNumber && (
            <div className="od-tracking">
              <h3>Tracking</h3>
              <p>{order.trackingNumber}</p>
            </div>
          )}
          {(order.status === 'PENDING' || order.status === 'CONFIRMED') && (
            <button className="btn btn-danger" onClick={handleCancel}>Cancel Order</button>
          )}
        </div>
      </div>
    </div>
  );
}

export { OrderList, OrderDetail };
